import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { useAppContext } from "utils/context";

import SendVideo from "./components/sendVideo";
import VideoErrorModal from "./components/videoErrorModal";
import VideoPermissionsModal from "./components/videoPermissionsModal";
import VideoTerm from "./components/videoTerm";

import { handleVideoData } from "containers/bankVideo/services/data.transformers";

import { postUploadVideo } from "containers/services/services";
import { handleStagesBankMetrix } from "containers/services/data.transformers";
import { WebengageGeneralButtonEvent } from "containers/services/events.data";

const VIDEO_STATUS = {
  SEND: "send",
  TERM: "term",
};

// tracking
const BankVideo = ({ currentStage, data, loading, onFinishVideo }) => {
  const { instance, metrixInstance } = useAppContext();
  const mediaRecorderRef = useRef(null);
  const webcamRef = useRef(null);
  const videoModalRef = useRef(null);
  const errorModalRef = useRef(null);

  const videoSrc = document.querySelector("#video-source");
  const videoTag = document.querySelector("#video-tag");

  const { fields = [], page_info: pageInfo = [] } = currentStage ?? {};

  const [capturing, setCapturing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [durationOfVideo, setDurationOfVideo] = useState(0);
  const [permissionCamera, setPermissionCamera] = useState(false);
  const [agreeAccessCamera, setAgreeAccessCamera] = useState(false);
  const [tryAgain, setTryAgain] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [steps, setSteps] = useState(VIDEO_STATUS.TERM);
  const [video, setVideo] = useState(null);
  const [permissionsError, setPermissionsError] = useState("");

  const handleNextStep = (step) => setSteps(step);

  const handleSubmitSendVideo = async () => {
    setUploading(true);
    const response = await postUploadVideo(
      instance,
      handleVideoData({ recordedChunks, type: data?.type })
    );

    if (`${response?.status}`.toLowerCase() === "success") {
      const keys = fields?.find((item) => item.key === "video_text");
      onFinishVideo({ [keys.key]: "" });
    }
    setUploading(false);
  };

  const handleGetPermissionCamera = () => {
    if ("permissions" in navigator) {
      // Permissions API is supported
      try {
        navigator.permissions
          ?.query({ name: "camera" })
          .then((result) => {
            if (result.state === "granted") {
              setPermissionCamera(true);
            } else if (result.state === "prompt") {
              errorModalRef.current?.showModal();
              setPermissionsError("card-page.camera-prompt");
            } else if (result.state === "denied") {
              errorModalRef.current?.showModal();
              setPermissionsError("card-page.camera-description");
            }
          })
          .catch(() => {
            errorModalRef.current?.showModal();
            setPermissionsError("card-page.permissions-not-supported");
          });
      } catch (error) {
        errorModalRef.current?.showModal();
        setPermissionsError("card-page.permissions-not-supported");
      }
    } else {
      errorModalRef.current?.showModal();
      setPermissionsError("card-page.permissions-not-supported");
    }
  };

  const handleHidePermissionsModal = () => {
    videoModalRef.current?.hideModal();
    if (!agreeAccessCamera) setAgreeAccessCamera(true);
  };

  const handleHideErrorModal = () => {
    errorModalRef.current?.hideModal();
  };

  const handleShowPermissionsModal = () => videoModalRef.current?.showModal();

  const handleStartCaptureClick = useCallback(() => {
    WebengageGeneralButtonEvent(
      "bank-button-video-verification",
      "",
      "button",
      {
        Type: "Bank",
        "Object id": "middleeast",
      }
    );
    if (permissionCamera) {
      setCapturing(true);
      const options = { videoBitsPerSecond: 250000 };
      if (MediaRecorder.isTypeSupported("video/webm; codecs=vp8")) {
        options.mimeType = "video/webm; codecs=vp8";
      } else if (MediaRecorder.isTypeSupported("video/webm")) {
        options.mimeType = "video/webm";
      } else if (MediaRecorder.isTypeSupported("video/mp4")) {
        options.mimeType = "video/mp4";
      } else {
        console.error("no suitable mimetype found for this device");
      }
      mediaRecorderRef.current = new MediaRecorder(
        webcamRef.current.stream,
        options
      );
      mediaRecorderRef.current.addEventListener(
        "dataavailable",
        handleDataAvailable
      );
      mediaRecorderRef.current.start();
      return;
    }

    handleShowPermissionsModal();
  }, [webcamRef, setCapturing, permissionCamera, mediaRecorderRef]);

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) setRecordedChunks((prev) => prev.concat(data));
    },
    [setRecordedChunks]
  );

  const handleStopCaptureClick = useCallback(
    (duration) => {
      setDurationOfVideo(duration);
      setTryAgain(true);
      mediaRecorderRef.current.stop();
      setCapturing(false);
    },
    [mediaRecorderRef, webcamRef, setCapturing]
  );

  const handleReadyForPreview = () => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: "video/mp4",
      });
      var reader = new FileReader();
      const myFile = new File([blob], "video.mp4", {
        type: blob.type,
      });
      reader.readAsDataURL(myFile);

      reader.onload = function (e) {
        videoSrc.src = e.target.result;
        videoTag.load();
      }.bind(this);
    }
  };

  const handleTryAgain = () => {
    setTryAgain(false);
    setRecordedChunks([]);
  };

  useEffect(() => {
    if (steps === VIDEO_STATUS.SEND) videoModalRef.current?.showModal();
  }, [steps]);

  useEffect(() => {
    if (videoTag && videoSrc && recordedChunks.length) handleReadyForPreview();
  }, [videoTag, videoSrc, recordedChunks]);

  useMemo(async () => {
    await handleStagesBankMetrix("BankVideo");
  }, [currentStage?.key]);

  const renderSteps = () => {
    switch (steps) {
      case VIDEO_STATUS.SEND:
        return (
          <SendVideo
            agreeAccessCamera={agreeAccessCamera}
            capturing={capturing}
            durationOfVideo={durationOfVideo}
            fields={fields}
            loading={loading || uploading}
            permissionCamera={permissionCamera}
            tryAgain={tryAgain}
            video={video}
            webcamRef={webcamRef}
            onGetPermissionCamera={handleGetPermissionCamera}
            onTryAgain={handleTryAgain}
            onNextStep={handleSubmitSendVideo}
            onStartCaptureClick={handleStartCaptureClick}
            onStopCaptureClick={handleStopCaptureClick}
          />
        );

      case VIDEO_STATUS.TERM:
      default:
        return (
          <VideoTerm
            pageInfo={pageInfo}
            onNextStep={() => handleNextStep(VIDEO_STATUS.SEND)}
          />
        );
    }
  };
  return (
    <div className="pt-12">
      <VideoPermissionsModal
        videoModalRef={videoModalRef}
        onClick={handleHidePermissionsModal}
      />

      <VideoErrorModal
        description={permissionsError}
        errorModalRef={errorModalRef}
        onClick={handleHideErrorModal}
      />
      {renderSteps()}
    </div>
  );
};
export default BankVideo;
